import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useStorytellerConcept, useAddSection, useUpdateSection, useDeleteSection } from '../../../queries';
import type { StoryOutlineSection } from '../../../domain';

interface Props {
  conceptId: string;
  activeSectionId?: string;
  onSelectSection: (section: StoryOutlineSection) => void;
}

type ChipColor = 'default' | 'info' | 'success';
const statusColor = (status: string): ChipColor =>
  status === 'polished' ? 'success' : status === 'drafted' ? 'info' : 'default';

/**
 * Storyteller outline (W-F9): collapsible concept summary + an ordered, selectable
 * list of sections. Clicking a section bubbles up so the shell can open it for
 * drafting. Reorder via up/down (no dnd dep) — swaps `sortOrder` with the neighbour.
 */
export const OutlineView: React.FC<Props> = ({ conceptId, activeSectionId, onSelectSection }) => {
  const { data: detail, isLoading } = useStorytellerConcept(conceptId);
  const addSection = useAddSection(conceptId);
  const updateSection = useUpdateSection(conceptId);
  const deleteSection = useDeleteSection(conceptId);
  const [summaryOpen, setSummaryOpen] = useState(true);

  const sections = useMemo(
    () => [...(detail?.outline ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [detail],
  );

  const move = (index: number, dir: 'up' | 'down') => {
    const j = dir === 'up' ? index - 1 : index + 1;
    if (j < 0 || j >= sections.length) return;
    const a = sections[index];
    const b = sections[j];
    updateSection.mutate({ sectionId: a.id, patch: { sortOrder: b.sortOrder } });
    updateSection.mutate({ sectionId: b.id, patch: { sortOrder: a.sortOrder } });
  };

  const handleAdd = () => {
    const nextOrder = sections.length;
    addSection.mutate({ title: `Section ${nextOrder + 1}`, synopsis: '', sortOrder: nextOrder });
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Loading outline…
        </Typography>
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          No concept selected.
        </Typography>
      </Box>
    );
  }

  const { concept } = detail;

  return (
    <Box sx={{ p: 1.5 }} aria-label="Story outline">
      {/* Concept summary (collapsible) */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" noWrap>
          {concept.name}
        </Typography>
        <IconButton
          size="small"
          aria-label={summaryOpen ? 'Collapse concept summary' : 'Expand concept summary'}
          onClick={() => setSummaryOpen(o => !o)}
        >
          {summaryOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>
      <Collapse in={summaryOpen}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {concept.premise}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {[concept.genre, concept.voice, concept.desiredLength]
            .filter(Boolean)
            .map((v, i) => (
              <Chip key={i} label={v} size="small" variant="outlined" />
            ))}
        </Stack>
      </Collapse>

      <Divider sx={{ my: 1 }} />

      {sections.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No sections yet. Generate an outline or add one below.
        </Typography>
      )}

      <Stack spacing={0.75}>
        {sections.map((section, index) => {
          const active = section.id === activeSectionId;
          return (
            <Box
              key={section.id}
              onClick={() => onSelectSection(section)}
              role="button"
              tabIndex={0}
              aria-label={`Section: ${section.title}`}
              sx={{
                p: 1,
                borderRadius: 1,
                cursor: 'pointer',
                border: theme => `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
                bgcolor: active ? 'action.selected' : 'background.paper',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                  {section.title}
                </Typography>
                <Chip
                  label={section.status}
                  color={statusColor(section.status)}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
                />
              </Stack>
              {section.synopsis && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {section.synopsis}
                </Typography>
              )}
              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mt: 0.25 }}>
                {section.estimatedWordCount != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    ~{section.estimatedWordCount} words
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Move section up"
                      disabled={index === 0}
                      onClick={e => {
                        e.stopPropagation();
                        move(index, 'up');
                      }}
                    >
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Move section down"
                      disabled={index === sections.length - 1}
                      onClick={e => {
                        e.stopPropagation();
                        move(index, 'down');
                      }}
                    >
                      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete section">
                  <IconButton
                    size="small"
                    aria-label="Delete section"
                    onClick={e => {
                      e.stopPropagation();
                      deleteSection.mutate(section.id);
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Box sx={{ mt: 1 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} disabled={addSection.isPending}>
          Add section
        </Button>
      </Box>
    </Box>
  );
};
